import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
} from "@mui/material";
import { RiCloseCircleFill } from "react-icons/ri";
import { LuDownload } from "react-icons/lu";
import { IoIosArrowForward } from "react-icons/io";
import { FaFileExcel } from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import axios from "axios";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fafafa",
    "&:hover": { backgroundColor: "#f5f5f5" },
    "&.Mui-focused": { backgroundColor: "#fff" },
  },
};

const labelSx = {
  mb: 1,
  display: "block",
  fontWeight: 600,
  fontSize: { xs: "13px", sm: "14px" },
  color: "#333",
};

const gradientBtnSx = {
  textTransform: "none",
  borderRadius: "8px",
  px: { xs: 2, sm: 3 },
  py: { xs: 1, sm: 1.25 },
};

export default function CreateMuhurt(props) {
  const {
    header = "Create Muhurt",
    button_name = "Create",
    row,
    onSave,
  } = props;

  const [muhurt, setMuhurt] = useState({
    title: row?.title || "",
    content: row?.body || "",
    muhurtStart: row?.muhurtStart ? dayjs(row.muhurtStart) : dayjs(),
    muhurtEnd: row?.muhurtEnd ? dayjs(row.muhurtEnd) : dayjs().add(1, "hour"),
    muhurtimage: row?.url || null,
  });

  const [errors, setErrors] = useState({});
  const [csvFile, setCsvFile] = useState(null);
  const [uploadMode, setUploadMode] = useState("single");

  useEffect(() => {
    if (row && row._id) {
      setMuhurt({
        title: row.title || "",
        content: row.body || "",
        muhurtStart: row.muhurtStart ? dayjs(row.muhurtStart) : dayjs(),
        muhurtEnd: row.muhurtEnd ? dayjs(row.muhurtEnd) : dayjs().add(1, "hour"),
        muhurtimage: row.url || null,
      });
      setUploadMode("single");
    } else if (!row) {
      setMuhurt({
        title: "",
        content: "",
        muhurtStart: dayjs(),
        muhurtEnd: dayjs().add(1, "hour"),
        muhurtimage: null,
      });
    }
  }, [row]);

  const validateForm = () => {
    const newErrors = {};

    if (!muhurt.title?.trim()) newErrors.title = "Title is required";
    if (!muhurt.content?.trim()) newErrors.content = "Content is required";
    if (!muhurt.muhurtStart) newErrors.muhurtStart = "Muhurt Start Date & Time is required";
    if (!muhurt.muhurtEnd) newErrors.muhurtEnd = "Muhurt End Date & Time is required";
    if (muhurt.muhurtStart && muhurt.muhurtEnd && muhurt.muhurtEnd.isBefore(muhurt.muhurtStart)) {
      newErrors.muhurtEnd = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg") {
        setMuhurt({ ...muhurt, muhurtimage: file });
        setErrors({ ...errors, notificationimage: "" });
      } else {
        alert("Please upload only PNG or JPG file");
        e.target.value = "";
      }
    }
  };

  const handleCSVExcelChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension === "csv" || fileExtension === "xlsx" || fileExtension === "xls") {
        setCsvFile(file);
        setErrors({ ...errors, csvFile: "" });
      } else {
        alert("Please upload only CSV or Excel (.xlsx, .xls) files");
        e.target.value = "";
      }
    }
  };

  const handleBulkSubmit = async () => {
    if (!csvFile) {
      alert("Please upload a CSV/Excel file");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("notificationfile", csvFile);
      formData.append("muhurtType", "multiple");

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/notification/createmuhurt`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.success) {
        alert(response.data.message || "Bulk upload completed successfully");
        if (onSave) onSave(false);
      } else {
        alert(response.data.message || "Failed to upload file");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert(error.response?.data?.message || "Failed to upload file");
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const form_data = new FormData();
      form_data.append("title", muhurt.title);
      form_data.append("body", muhurt.content);
      form_data.append("content", muhurt.content);
      form_data.append("muhurtStart", muhurt.muhurtStart.valueOf());
      form_data.append("muhurtEnd", muhurt.muhurtEnd.valueOf());
      form_data.append("muhurtType", "single");

      if (row?._id) form_data.append("id", row._id.toString());

      if (muhurt.muhurtimage instanceof File) {
        form_data.append("muhurtimage", muhurt.muhurtimage);
      } else if (muhurt.muhurtimage) {
        form_data.append("muhurtimage", muhurt.muhurtimage);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/muhurt/create`,
        form_data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.success) {
        alert(response.data.message || "Muhurt saved successfully");
        if (onSave) onSave(false);
      } else {
        alert(response.data.message || "Failed to save Muhurt");
      }
    } catch (error) {
      console.error("Error saving Muhurt:", error);
      alert(error.response?.data?.message || "Failed to save Muhurt");
    }
  };

  const modeButtonSx = (active) => ({
    ...gradientBtnSx,
    flex: { xs: 1, sm: "none" },
    width: { xs: "100%", sm: "auto" },
    background: active
      ? "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)"
      : "transparent",
    borderColor: "#752ADA",
    color: active ? "#fff" : "#752ADA",
  });

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        maxHeight: { xs: "85vh", sm: "90vh" },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Typography
        fontWeight={700}
        fontSize={{ xs: 18, sm: 22, md: 24 }}
        fontFamily="Aeonik"
        sx={{
          background: "linear-gradient(135deg, #752ADA 0%, #9b4dff 100%)",
          color: "#fff",
          p: { xs: 1.5, sm: 2 },
          borderRadius: "8px 8px 0 0",
          flexShrink: 0,
        }}
      >
        {header}
      </Typography>

      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          backgroundColor: "#fff",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          flex: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            mb: 3,
            p: 1,
            borderRadius: "12px",
            bgcolor: "#faf8ff",
            border: "1px solid #ece6f8",
          }}
        >
          <Button
            variant={uploadMode === "single" ? "contained" : "outlined"}
            onClick={() => {
              setUploadMode("single");
              setCsvFile(null);
              setMuhurt({
                title: row?.title || "",
                content: row?.body || "",
                muhurtStart: row?.muhurtStart ? dayjs(row.muhurtStart) : dayjs(),
                muhurtEnd: row?.muhurtEnd ? dayjs(row.muhurtEnd) : dayjs().add(1, "hour"),
                muhurtimage: row?.url || null,
              });
            }}
            sx={modeButtonSx(uploadMode === "single")}
          >
            Single Entry
          </Button>
          <Button
            variant={uploadMode === "bulk" ? "contained" : "outlined"}
            onClick={() => {
              setUploadMode("bulk");
              setMuhurt({
                title: "",
                content: "",
                muhurtStart: dayjs(),
                muhurtEnd: dayjs().add(1, "hour"),
                muhurtimage: null,
              });
            }}
            sx={modeButtonSx(uploadMode === "bulk")}
          >
            Bulk Upload (CSV/Excel)
          </Button>
        </Stack>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {uploadMode === "single" ? (
            <FormControl fullWidth>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12}>
                  <FormLabel required sx={labelSx}>Muhurt Title</FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter Muhurt Title"
                    value={muhurt.title}
                    onChange={(e) => {
                      setMuhurt({ ...muhurt, title: e.target.value });
                      setErrors({ ...errors, title: "" });
                    }}
                    error={!!errors.title}
                    helperText={errors.title}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormLabel required sx={labelSx}>Muhurt Content</FormLabel>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={12}
                    size="small"
                    placeholder="Enter Muhurt Content"
                    value={muhurt.content}
                    onChange={(e) => {
                      setMuhurt({ ...muhurt, content: e.target.value });
                      setErrors({ ...errors, content: "" });
                    }}
                    error={!!errors.content}
                    helperText={errors.content}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormLabel required sx={labelSx}>Muhurt Start Date & Time</FormLabel>
                  <DateTimePicker
                    label="Select Start Date & Time"
                    format="DD/MM/YYYY hh:mm A"
                    value={muhurt.muhurtStart}
                    onChange={(newValue) => {
                      setMuhurt({ ...muhurt, muhurtStart: newValue });
                      setErrors({ ...errors, muhurtStart: "" });
                    }}
                    ampm
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: !!errors.muhurtStart,
                        helperText: errors.muhurtStart,
                        sx: fieldSx,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormLabel required sx={labelSx}>Muhurt End Date & Time</FormLabel>
                  <DateTimePicker
                    label="Select End Date & Time"
                    format="DD/MM/YYYY hh:mm A"
                    value={muhurt.muhurtEnd}
                    onChange={(newValue) => {
                      setMuhurt({ ...muhurt, muhurtEnd: newValue });
                      setErrors({ ...errors, muhurtEnd: "" });
                    }}
                    ampm
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: !!errors.muhurtEnd,
                        helperText: errors.muhurtEnd,
                        sx: fieldSx,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormLabel sx={labelSx}>Image (Optional)</FormLabel>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{
                      color: "#752ADA",
                      border: "2px dashed #752ada",
                      textTransform: "none",
                      borderRadius: "8px",
                      px: 3,
                      py: { xs: 1.5, sm: 2 },
                      backgroundColor: "rgba(117, 42, 218, 0.05)",
                      "&:hover": {
                        borderColor: "#752ADA",
                        backgroundColor: "rgba(117, 42, 218, 0.1)",
                        borderStyle: "solid",
                      },
                    }}
                  >
                    <LuDownload style={{ marginRight: 8, fontSize: "18px" }} />
                    Choose Image
                    <input
                      type="file"
                      hidden
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleImageChange}
                    />
                  </Button>

                  {errors.notificationimage && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                      {errors.notificationimage}
                    </Typography>
                  )}

                  {muhurt.muhurtimage && (
                    <Box
                      sx={{
                        mt: 2,
                        p: { xs: 1.5, sm: 2.5 },
                        border: "2px solid #e0e0e0",
                        borderRadius: "12px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        position: "relative",
                        backgroundColor: "#fafafa",
                        width: "100%",
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          setMuhurt({ ...muhurt, muhurtimage: null });
                          setErrors({ ...errors, notificationimage: "" });
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(255,255,255,0.95)",
                        }}
                        size="small"
                      >
                        <RiCloseCircleFill style={{ color: "#f44336", fontSize: "20px" }} />
                      </IconButton>
                      <Box
                        component="img"
                        src={
                          muhurt.muhurtimage instanceof File
                            ? URL.createObjectURL(muhurt.muhurtimage)
                            : muhurt.muhurtimage
                        }
                        alt="Preview"
                        sx={{
                          maxWidth: "100%",
                          maxHeight: { xs: 200, sm: 280, md: 300 },
                          borderRadius: "8px",
                          objectFit: "contain",
                        }}
                      />
                      {muhurt.muhurtimage instanceof File && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, textAlign: "center" }}>
                          {muhurt.muhurtimage.name}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </FormControl>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <FormLabel required sx={labelSx}>Upload CSV/Excel File</FormLabel>
                <Typography variant="caption" sx={{ display: "block", mb: 2, color: "#666" }}>
                  File should contain columns: title, content, startdate, starttime, enddate, endtime
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{
                    color: "#752ADA",
                    border: "2px dashed #752ada",
                    textTransform: "none",
                    borderRadius: "8px",
                    py: { xs: 1.5, sm: 2 },
                    backgroundColor: "rgba(117, 42, 218, 0.05)",
                  }}
                >
                  <FaFileExcel style={{ marginRight: 8, fontSize: "18px" }} />
                  Choose CSV/Excel File
                  <input
                    type="file"
                    hidden
                    accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    onChange={handleCSVExcelChange}
                  />
                </Button>
                {csvFile && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="body2" sx={{ color: "#666", flex: 1, wordBreak: "break-all" }}>
                      Selected: {csvFile.name}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setCsvFile(null);
                        setErrors({ ...errors, csvFile: "" });
                      }}
                      sx={{ color: "#f44336", alignSelf: { xs: "flex-end", sm: "center" } }}
                      size="small"
                    >
                      <RiCloseCircleFill />
                    </IconButton>
                  </Stack>
                )}
              </Grid>
            </Grid>
          )}
        </LocalizationProvider>

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          justifyContent="flex-end"
          spacing={1.5}
          sx={{ mt: 4, pt: 2, borderTop: "1px solid #e0e0e0" }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSave?.(false)}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              width: { sm: "auto" },
              minWidth: { sm: 100 },
              borderColor: "#ccc",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={uploadMode === "single" ? handleSubmit : handleBulkSubmit}
            disabled={uploadMode === "bulk" && !csvFile}
            sx={{
              background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
              fontSize: { xs: "14px", sm: "15px" },
              px: { xs: 2, sm: 4 },
              textTransform: "none",
              borderRadius: "8px",
              width: { sm: "auto" },
              minWidth: { sm: 140 },
            }}
          >
            {uploadMode === "single" ? button_name : "Upload File"}
            <IoIosArrowForward style={{ marginLeft: 8 }} />
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
